import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectAuth, selectUser } from '../redux/main/authSlice';
import { selectAccountData } from '../redux/main/accountSlice';
import DynamicTitle from '../components/DynamicTitle';
import { format, parseISO, startOfMonth, endOfMonth, addMonths, subMonths, isEqual, isSameDay, getDaysInMonth } from 'date-fns';
import { it } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

export default function EmployeeHoursInsight() {
  // Stati per la gestione dei dati
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Hook per la navigazione
  const navigate = useNavigate();
  
  // Recupero token, user e account dal Redux store
  const auth = useSelector(selectAuth);
  const token = auth?.token;
  const user = useSelector(selectUser);
  const accountData = useSelector(selectAccountData);
  
  // Funzioni per la navigazione tra i mesi
  const goToPreviousMonth = () => {
    setSelectedMonth(prevMonth => subMonths(prevMonth, 1));
  };
  
  const goToNextMonth = () => {
    setSelectedMonth(prevMonth => addMonths(prevMonth, 1));
  };
  
  // Formattazione date
  const formatMonthYear = (date) => {
    return format(date, 'MMMM yyyy', { locale: it });
  };
  
  const formatDate = (date) => {
    return format(new Date(date), 'EEEE d MMMM', { locale: it });
  };
  
  const formatTime = (dateString) => {
    return format(new Date(dateString), 'HH:mm');
  };
  
  // Funzione per recuperare gli appuntamenti del mese selezionato
  const fetchMonthAppointments = async () => {
    if (!token || !user?.userId || !accountData?._id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Calcolo la data di inizio (primo giorno del mese)
      const fromDate = startOfMonth(selectedMonth);
      
      // Calcolo la data di fine (ultimo giorno del mese)
      const toDate = endOfMonth(selectedMonth);
      
      // Preparo i parametri di query
      const params = new URLSearchParams({
        accountId: accountData._id,
        userId: user.userId,
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString()
      });
      
      // Chiamata all'API
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/appointments/byuser?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error(`Errore nel recupero degli appuntamenti: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Ordino gli appuntamenti per data
      const sortedAppointments = data.sort((a, b) => new Date(a.fromDate) - new Date(b.fromDate));
      setAppointments(sortedAppointments);
    } catch (err) {
      console.error('Errore durante il recupero degli appuntamenti:', err);
      setError('Impossibile caricare gli appuntamenti. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };
  
  // Effetto per recuperare gli appuntamenti quando cambia il mese selezionato
  useEffect(() => {
    fetchMonthAppointments();
  }, [selectedMonth, user?.userId, accountData?._id, token]);
  
  // Funzione per normalizzare la visualizzazione delle ore e minuti
  const formatHoursAndMinutes = (hours, minutes) => {
    // Converti tutto in minuti
    const totalMinutes = (hours * 60) + minutes;
    
    // Riconverti in ore e minuti normalizzati
    const normalizedHours = Math.floor(totalMinutes / 60);
    const normalizedMinutes = totalMinutes % 60;
    
    return `${normalizedHours}h ${normalizedMinutes}m`;
  };
  
  // Calcola le ore totali lavorate nel mese
  const monthlyHoursSummary = useMemo(() => {
    if (!appointments.length) return { hours: 0, minutes: 0, formatted: '0h 0m' };
    
    let totalHours = 0;
    let totalMinutes = 0;
    
    appointments.forEach(appointment => {
      // Verifica se l'appuntamento ha dettagli di chiusura per l'utente corrente
      if (appointment.closingDetails && appointment.closingDetails.length > 0) {
        const userClosingDetail = appointment.closingDetails.find(
          detail => detail.userId === user.userId
        );
        
        if (userClosingDetail) {
          totalHours += userClosingDetail.hours || 0;
          totalMinutes += userClosingDetail.minutes || 0;
        }
      }
    });
    
    // Normalizza i minuti (converti eccessi di 60 minuti in ore)
    totalHours += Math.floor(totalMinutes / 60);
    totalMinutes = totalMinutes % 60;
    
    return {
      hours: totalHours,
      minutes: totalMinutes,
      formatted: formatHoursAndMinutes(totalHours, totalMinutes)
    };
  }, [appointments, user?.userId]);
  
  // Organizza gli appuntamenti per giorno
  const appointmentsByDay = useMemo(() => {
    // Crea un array per tutti i giorni del mese
    const daysInMonth = getDaysInMonth(selectedMonth);
    const days = Array.from({ length: daysInMonth }, (_, i) => {
      const day = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), i + 1);
      return {
        date: day,
        appointments: [],
        hoursWorked: 0,
        minutesWorked: 0,
        hasUnreportedAppointments: false,
        hasInsufficientHours: false
      };
    });
    
    // Distribuisci gli appuntamenti nei giorni corrispondenti
    appointments.forEach(appointment => {
      const appointmentDate = new Date(appointment.fromDate);
      const dayIndex = appointmentDate.getDate() - 1;
      
      // Verifica se il giorno esiste nell'array (controllo di sicurezza)
      if (dayIndex >= 0 && dayIndex < days.length) {
        days[dayIndex].appointments.push(appointment);
        
        // Controlla se l'utente ha registrato ore per questo appuntamento
        let userHoursReported = false;
        
        if (appointment.closingDetails && appointment.closingDetails.length > 0) {
          const userClosingDetail = appointment.closingDetails.find(
            detail => detail.userId === user.userId
          );
          
          if (userClosingDetail) {
            userHoursReported = true;
            days[dayIndex].hoursWorked += userClosingDetail.hours || 0;
            days[dayIndex].minutesWorked += userClosingDetail.minutes || 0;
          }
        }
        
        // Verifica se l'appuntamento non ha ore registrate
        if (!userHoursReported) {
          days[dayIndex].hasUnreportedAppointments = true;
        }
      }
    });
    
    // Normalizzazione ore e minuti e flag per ore insufficienti
    days.forEach(day => {
      // Converti eccessi di 60 minuti in ore
      day.hoursWorked += Math.floor(day.minutesWorked / 60);
      day.minutesWorked = day.minutesWorked % 60;
      
      // Verifica se il giorno ha meno di 8 ore registrate e ha appuntamenti
      if (day.appointments.length > 0 && day.hoursWorked < 8) {
        day.hasInsufficientHours = true;
      }
      
      // Aggiunge la versione formattata
      day.formattedHours = formatHoursAndMinutes(day.hoursWorked, day.minutesWorked);
    });
    
    // Ritorna solo i giorni con appuntamenti
    return days.filter(day => day.appointments.length > 0);
  }, [appointments, user?.userId, selectedMonth]);
  
  // Funzione per navigare al dettaglio dell'appuntamento
  const goToAppointmentDetail = (appointmentId) => {
    navigate(`/employee-appointment-detail/${appointmentId}`);
  };

  return (
    <div className="p-4 max-w-screen-xl mx-auto">
      <DynamicTitle title="Analisi Ore Lavorate" />
      
      {/* Selezione del mese */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Analisi Ore Lavorate</h1>
        
        <div className="flex items-center space-x-4">
          <button 
            onClick={goToPreviousMonth}
            className="p-2 rounded-full hover:bg-gray-200 transition-colors"
          >
            <i className="fi fi-br-angle-left"></i>
          </button>
          
          <span className="text-xl font-medium min-w-[200px] text-center">
            {formatMonthYear(selectedMonth)}
          </span>
          
          <button 
            onClick={goToNextMonth}
            className="p-2 rounded-full hover:bg-gray-200 transition-colors"
          >
            <i className="fi fi-br-angle-right"></i>
          </button>
        </div>
      </div>
      
      {loading && (
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}
      
      {!loading && !error && (
        <>
          {/* Riepilogo delle ore mensili */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Ore Totali del Mese</h2>
            
            <div className="flex items-center">
              <div className="bg-blue-100 rounded-full p-4 mr-5">
                <i className="fi fi-br-clock text-blue-600 text-2xl"></i>
              </div>
              
              <div>
                <span className="text-3xl font-bold">{monthlyHoursSummary.formatted}</span>
                <p className="text-gray-500 text-sm mt-1">Ore totali registrate in {formatMonthYear(selectedMonth)}</p>
              </div>
            </div>
          </div>
          
          {/* Dettaglio giornaliero */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Dettaglio Giornaliero</h2>
            
            {appointmentsByDay.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nessun appuntamento trovato in questo mese.
              </div>
            ) : (
              <div className="space-y-6">
                {appointmentsByDay.map((day) => (
                  <div key={day.date.toString()} className="border-b pb-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-md font-medium capitalize">{formatDate(day.date)}</h3>
                      
                      <div className="flex items-center">
                        <span className={`text-lg font-semibold ${day.hasInsufficientHours ? 'text-orange-500' : 'text-green-600'}`}>
                          {day.formattedHours}
                        </span>
                        
                        {day.hasUnreportedAppointments && (
                          <span className="ml-2 bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs font-medium">
                            Ore non registrate
                          </span>
                        )}
                        
                        {day.hasInsufficientHours && day.hoursWorked > 0 && (
                          <span className="ml-2 bg-orange-100 text-orange-600 px-2 py-1 rounded-full text-xs font-medium">
                            &lt; 8 ore
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <ul className="space-y-2">
                      {day.appointments.map((appointment) => {
                        // Verifica se l'utente ha registrato ore per questo appuntamento
                        const userClosingDetail = appointment.closingDetails?.find(
                          detail => detail.userId === user.userId
                        );
                        
                        return (
                          <li 
                            key={appointment._id} 
                            className={`p-3 rounded-lg cursor-pointer transition-colors ${userClosingDetail ? 'bg-gray-50 hover:bg-gray-100' : 'bg-red-50 hover:bg-red-100'}`}
                            onClick={() => goToAppointmentDetail(appointment._id)}
                          >
                            <div className="flex justify-between">
                              <div>
                                <h4 className="font-medium">{appointment.title || 'Appuntamento senza titolo'}</h4>
                                
                                <div className="text-sm text-gray-500 mt-1">
                                  <span>{formatTime(appointment.fromDate)} - {formatTime(appointment.toDate)}</span>
                                  
                                  {appointment.jobLabel && (
                                    <span className="ml-2 text-blue-600">
                                      <i className="fi fi-br-briefcase mr-1"></i>
                                      {appointment.jobLabel}
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {userClosingDetail ? (
                                <div className="text-green-600 font-medium">
                                  {formatHoursAndMinutes(userClosingDetail.hours || 0, userClosingDetail.minutes || 0)}
                                </div>
                              ) : (
                                <div className="text-red-500 font-medium">
                                  Ore non registrate
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
